/**
 * Table Width Calculation Utilities
 * 
 * Provides width calculation and distribution algorithms for schema-driven tables.
 * Handles pixel/percentage based widths, proportional scaling, and fill behavior.
 * 
 * Constitutional Compliance: Pure presentation logic functions - no business state management.
 */

/**
 * Calculates final column widths based on schema specifications and container constraints
 * @param {Array} columnSchemas - Array of column schema objects with optional width properties
 * @param {Number} containerWidth - Available container width in pixels
 * @returns {Object} Width distribution result with calculated values
 */
export function calculateColumnWidths(columnSchemas, containerWidth) {
  const fixedColumns = []
  const percentageColumns = []
  const autoColumns = []
  let fillColumn = null
  
  // Categorize columns by width type
  columnSchemas.forEach((schema, index) => {
    if (!schema.width) {
      autoColumns.push({ schema, index })
    } else if (schema.width === '100%') {
      if (!fillColumn) {
        fillColumn = { schema, index }
      }
      // Multiple fill columns: use first, ignore others
    } else if (schema.width.endsWith('px')) {
      const pixels = parseInt(schema.width.replace('px', ''))
      fixedColumns.push({ schema, index, pixels })
    } else if (schema.width.endsWith('%')) {
      const percentage = parseInt(schema.width.replace('%', ''))
      percentageColumns.push({ schema, index, percentage })
    } else {
      // Invalid width format - treat as auto
      autoColumns.push({ schema, index })
    }
  })
  
  return distributeWidths({
    fixedColumns,
    percentageColumns,
    autoColumns,
    fillColumn,
    containerWidth,
    totalColumns: columnSchemas.length
  })
}

/**
 * Distributes available width across different column types
 * @param {Object} columnCategories - Categorized columns and container info
 * @returns {Object} Final width distribution
 */
function distributeWidths({ fixedColumns, percentageColumns, autoColumns, fillColumn, containerWidth, totalColumns }) {
  const result = {
    widths: new Array(totalColumns).fill(null),
    totalUsedWidth: 0,
    scalingApplied: false,
    warnings: []
  }
  
  // Calculate fixed width totals
  const fixedWidthTotal = fixedColumns.reduce((sum, col) => sum + col.pixels, 0)
  
  // Calculate percentage width totals  
  const percentageTotal = percentageColumns.reduce((sum, col) => sum + col.percentage, 0)
  const percentageWidthTotal = Math.round(containerWidth * (percentageTotal / 100))
  
  const directUsedWidth = fixedWidthTotal + percentageWidthTotal
  
  // Check if direct specifications exceed container
  if (directUsedWidth > containerWidth) {
    return handleWidthOverflow({
      fixedColumns,
      percentageColumns,
      autoColumns,
      fillColumn,
      containerWidth,
      result
    })
  }
  
  // Apply fixed widths
  fixedColumns.forEach(col => {
    result.widths[col.index] = col.pixels + 'px'
  })
  
  // Apply percentage widths
  percentageColumns.forEach(col => {
    const pixels = Math.round(containerWidth * (col.percentage / 100))
    result.widths[col.index] = pixels + 'px'
  })
  
  result.totalUsedWidth = directUsedWidth
  
  // Handle remaining space distribution
  const remainingWidth = containerWidth - directUsedWidth
  
  if (fillColumn) {
    // Fill column gets remaining space after fixed and percentage allocations
    
    if (autoColumns.length > 0) {
      // When both fill and auto columns exist, give auto columns minimal space
      const minAutoWidth = 80 // Minimum width for auto columns
      const totalAutoMinWidth = autoColumns.length * minAutoWidth
      
      if (remainingWidth > totalAutoMinWidth) {
        // Assign minimal widths to auto columns, rest to fill column
        autoColumns.forEach(col => {
          result.widths[col.index] = minAutoWidth + 'px'
          result.totalUsedWidth += minAutoWidth
        })
        
        const fillWidth = remainingWidth - totalAutoMinWidth
        result.widths[fillColumn.index] = Math.max(100, fillWidth) + 'px' // Minimum 100px for fill
        result.totalUsedWidth += fillWidth
      } else {
        // Insufficient space: distribute proportionally
        const autoWidth = Math.floor(remainingWidth * 0.3 / autoColumns.length) // 30% to auto columns
        const fillWidth = remainingWidth - (autoWidth * autoColumns.length)
        
        autoColumns.forEach(col => {
          result.widths[col.index] = Math.max(50, autoWidth) + 'px'
        })
        
        result.widths[fillColumn.index] = Math.max(100, fillWidth) + 'px'
        result.totalUsedWidth += remainingWidth
      }
    } else {
      // Only fill column: takes all remaining space
      const fillWidth = Math.max(100, remainingWidth) // Minimum 100px
      result.widths[fillColumn.index] = fillWidth + 'px'
      result.totalUsedWidth += fillWidth
    }
  } else if (autoColumns.length > 0) {
    // No fill column: distribute remaining space equally among auto columns
    const autoWidth = Math.floor(remainingWidth / autoColumns.length)
    
    autoColumns.forEach(col => {
      result.widths[col.index] = Math.max(50, autoWidth) + 'px'
    })
    
    result.totalUsedWidth += autoWidth * autoColumns.length
  }
  
  // Add warnings if fill column conflicts detected
  if (fillColumn && percentageColumns.some(col => col.percentage === 100)) {
    result.warnings.push('Fill column (100%) specified alongside 100% percentage column - behavior might be unexpected')
  }
  
  return result
}

/**
 * Handles width overflow scenarios by applying proportional scaling
 * @param {Object} params - Column categories and container info
 * @returns {Object} Scaled width distribution
 */
function handleWidthOverflow({ fixedColumns, percentageColumns, autoColumns, fillColumn, containerWidth, result }) {
  result.scalingApplied = true
  result.warnings.push('Column widths exceed container - proportional scaling applied')
  
  // Calculate total "weight" for scaling
  const fixedWeight = fixedColumns.reduce((sum, col) => sum + col.pixels, 0)
  const percentageWeight = percentageColumns.reduce((sum, col) => {
    return sum + Math.round(containerWidth * (col.percentage / 100))
  }, 0)
  
  const totalWeight = fixedWeight + percentageWeight
  const scalingFactor = containerWidth / totalWeight
  
  // Apply scaled widths
  fixedColumns.forEach(col => {
    const scaledWidth = Math.round(col.pixels * scalingFactor)
    result.widths[col.index] = scaledWidth + 'px'
    result.totalUsedWidth += scaledWidth
  })
  
  percentageColumns.forEach(col => {
    const originalPixels = Math.round(containerWidth * (col.percentage / 100))
    const scaledWidth = Math.round(originalPixels * scalingFactor)
    result.widths[col.index] = scaledWidth + 'px'
    result.totalUsedWidth += scaledWidth
  })
  
  // Auto and fill columns get no space in overflow scenarios
  autoColumns.forEach(col => {
    result.widths[col.index] = '50px' // Minimum width
  })
  
  if (fillColumn) {
    result.widths[fillColumn.index] = '50px' // Minimum width
  }
  
  return result
}

/**
 * Enforces minimum width constraints for column usability
 * @param {Array} widths - Calculated width values
 * @param {Number} minWidth - Minimum width in pixels (default: 50)
 * @returns {Array} Width values with minimum constraints applied
 */
export function enforceMinimumWidths(widths, minWidth = 50) {
  return widths.map(width => {
    if (!width) return width
    
    const pixels = parseInt(width.replace('px', ''))
    if (pixels < minWidth) {
      return minWidth + 'px'
    }
    return width
  })
}

/**
 * Calculates responsive behavior for container width changes
 * @param {Array} columnSchemas - Column schema specifications
 * @param {Number} oldWidth - Previous container width
 * @param {Number} newWidth - New container width
 * @returns {Object} Responsive width adjustments
 */
export function calculateResponsiveWidths(columnSchemas, oldWidth, newWidth) {
  // Recalculate all widths for new container size
  const newWidths = calculateColumnWidths(columnSchemas, newWidth)
  
  return {
    ...newWidths,
    widthChange: newWidth - oldWidth,
    scalingRatio: newWidth / oldWidth,
    requiresUpdate: Math.abs(newWidth - oldWidth) > 10 // Only update for significant changes
  }
}

/**
 * Performance monitoring for large table scenarios
 * @param {Number} columnCount - Number of columns being processed
 * @param {Number} calculationTime - Time taken for calculations in ms
 * @returns {Object} Performance metrics and warnings
 */
export function monitorPerformance(columnCount, calculationTime) {
  const performance = {
    columnCount,
    calculationTime,
    warnings: []
  }
  
  if (columnCount > 25) {
    performance.warnings.push(`Large table detected: ${columnCount} columns may impact performance`)
  }
  
  if (calculationTime > 100) {
    performance.warnings.push(`Slow width calculation: ${calculationTime}ms exceeds 100ms target`)
  }
  
  return performance
}

/**
 * Detects and validates fill column configurations
 * @param {Array} columnSchemas - Array of column schema objects
 * @returns {Object} Fill column analysis and validation results
 */
export function detectFillColumns(columnSchemas) {
  const fillColumns = []
  const warnings = []
  
  columnSchemas.forEach((schema, index) => {
    if (schema.width === '100%') {
      fillColumns.push({ schema, index })
    }
  })
  
  // Validation checks
  if (fillColumns.length > 1) {
    warnings.push(`Multiple columns set to "100%" width - only the first will be used as fill column`)
    if (process.env.NODE_ENV === 'development') {
      console.warn('Multiple columns set to "100%" width', fillColumns.map(col => col.schema.key || col.schema.label))
    }
  }
  
  return {
    fillColumns,
    primaryFillColumn: fillColumns[0] || null,
    hasFillColumn: fillColumns.length > 0,
    warnings
  }
}

/**
 * Calculates remaining space available for fill columns
 * @param {Number} containerWidth - Total available container width
 * @param {Array} fixedWidths - Array of fixed pixel widths
 * @param {Array} percentageWidths - Array of percentage values
 * @param {Number} autoColumnCount - Number of auto-sizing columns
 * @param {Number} minAutoWidth - Minimum width per auto column (default: 80px)
 * @returns {Object} Remaining space analysis
 */
export function calculateRemainingSpace(containerWidth, fixedWidths = [], percentageWidths = [], autoColumnCount = 0, minAutoWidth = 80) {
  const fixedTotal = fixedWidths.reduce((sum, width) => sum + width, 0)
  const percentageTotal = percentageWidths.reduce((sum, percent) => {
    return sum + Math.round(containerWidth * (percent / 100))
  }, 0)
  
  const reservedAutoSpace = autoColumnCount * minAutoWidth
  const directlyUsed = fixedTotal + percentageTotal + reservedAutoSpace
  
  const remainingSpace = Math.max(0, containerWidth - directlyUsed)
  
  return {
    containerWidth,
    fixedTotal,
    percentageTotal,
    reservedAutoSpace,
    directlyUsed,
    remainingSpace,
    fillSpaceAvailable: remainingSpace > 0,
    spaceUtilization: (directlyUsed / containerWidth) * 100
  }
}

/**
 * Optimizes fill column behavior for responsive scenarios
 * @param {Object} fillAnalysis - Result from detectFillColumns
 * @param {Object} spaceAnalysis - Result from calculateRemainingSpace
 * @param {Number} containerWidth - Current container width
 * @returns {Object} Optimized fill column configuration
 */
export function optimizeFillBehavior(fillAnalysis, spaceAnalysis, containerWidth) {
  if (!fillAnalysis.hasFillColumn) {
    return {
      hasFillOptimization: false,
      recommendation: 'No fill column detected'
    }
  }
  
  const recommendations = []
  const optimization = {
    hasFillOptimization: true,
    fillColumnIndex: fillAnalysis.primaryFillColumn.index,
    recommendedMinWidth: 150, // Minimum recommended width for fill columns
    recommendations
  }
  
  // Analysis and recommendations
  if (spaceAnalysis.remainingSpace < 150) {
    recommendations.push('Limited space for fill column - consider reducing fixed column widths')
  }
  
  if (spaceAnalysis.spaceUtilization > 90) {
    recommendations.push('High space utilization detected - fill column may have minimal space')
  }
  
  if (containerWidth < 600) {
    recommendations.push('Small container detected - consider responsive breakpoints for fill column')
    optimization.recommendedMinWidth = 100 // Reduce minimum on small screens
  }
  
  return optimization
}